import Fuse from 'fuse.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AthleteAvatar } from '../components/athlete_avatar';
import { publicApi } from '../data/api';
import { PageSection } from '../components/page_section';
import { LoadingSkeleton, StatusPanel } from '../components/status_panel';
import {
  formatMetric,
  getDisplayName,
  getShortAthleteId,
  normalizeAthleteId,
} from '../lib/result_utils';
import type { LeaderboardRow } from '../types/result';

export function LeaderboardPage() {
  const [searchParams] = useSearchParams();
  const highlightedId = searchParams.get('athleteId') ?? '';
  const [rows, setRows] = useState<LeaderboardRow[]>([]);
  const [search, setSearch] = useState('');
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [retryKey, setRetryKey] = useState(0);
  const highlightRef = useRef<HTMLTableRowElement>(null);
  const fuzzySearch = useMemo(
    () =>
      new Fuse(rows, {
        keys: [
          {
            name: 'fullName',
            getFn: (row) => getDisplayName(row),
          },
          'externalId',
          'externalIdNoWithoutText',
        ],
        threshold: 0.35,
        ignoreLocation: true,
      }),
    [rows],
  );
  const filteredRows = useMemo(() => {
    const query = search.trim();
    if (!query) {
      return rows;
    }

    const normalizedQuery = query.toLowerCase();
    const exactMatches = rows.filter((row) =>
      [
        getDisplayName(row),
        row.externalId,
        row.externalIdNoWithoutText,
      ].some((value) => value.toLowerCase().includes(normalizedQuery)),
    );

    return exactMatches.length > 0
      ? exactMatches
      : fuzzySearch.search(query).map(({ item }) => item);
  }, [fuzzySearch, rows, search]);

  useEffect(() => {
    let isCurrent = true;
    void publicApi
      .getLeaderboard()
      .then((results) => {
        if (isCurrent) {
          setRows(results);
          setLoadState('ready');
        }
      })
      .catch(() => {
        if (isCurrent) setLoadState('error');
      });
    return () => {
      isCurrent = false;
    };
  }, [retryKey]);

  const isHighlighted = (row: LeaderboardRow) => {
    const target = normalizeAthleteId(highlightedId);
    return Boolean(target) && (
      normalizeAthleteId(row.externalId) === target ||
      normalizeAthleteId(getShortAthleteId(row.externalId)) === target
    );
  };

  useEffect(() => {
    if (loadState === 'ready' && highlightedId) {
      highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      highlightRef.current?.focus({ preventScroll: true });
    }
  }, [highlightedId, loadState]);

  if (loadState === 'loading') {
    return (
      <div className="app-gradient min-h-[calc(100dvh-3.5rem)]">
        <PageSection className="py-8">
          <LoadingSkeleton label="Loading leaderboard" />
        </PageSection>
      </div>
    );
  }

  if (loadState === 'error') {
    return (
      <PageSection className="app-gradient min-h-[calc(100dvh-3.5rem)] py-8">
        <StatusPanel
          title="We could not load the leaderboard"
          tone="error"
          action={<button type="button" onClick={() => { setLoadState('loading'); setRetryKey((value) => value + 1); }} className="button-primary px-5 py-3 font-bold">Try again</button>}
        >
          The results service may be temporarily unavailable.
        </StatusPanel>
      </PageSection>
    );
  }

  return (
    <div className="app-gradient min-h-[calc(100dvh-3.5rem)] px-3 py-10 sm:px-6 sm:py-14">
      <div className="mx-auto max-w-5xl">
        <p className="text-center text-xs font-black uppercase tracking-[0.2em] text-qs-indigo">
          Combine analytics
        </p>
        <h1 className="mt-2 bg-gradient-to-r from-qs-navy via-qs-indigo to-qs-violet bg-clip-text text-center text-3xl font-black text-transparent">
          Leaderboard
        </h1>
        <div className="surface-card mx-auto mt-7 max-w-xl p-4">
          <label htmlFor="leaderboard-search" className="block text-sm font-black text-qs-navy">
            Search the leaderboard
          </label>
          <input
            id="leaderboard-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name or athlete ID"
            autoComplete="off"
            className="input-themed mt-2 w-full px-4 py-3 text-base outline-none focus-visible:outline-none"
            aria-label="Search athlete or ID"
          />
        </div>

      {
        rows.length > 0 && (
          <p className="mt-7 text-right text-xs font-grey-500 text-qs-navy">
            Drag to scroll horizontally on smaller screens
          </p>
        )
      }

      {rows.length === 0 ? (
        <div className="mt-8"><StatusPanel title="No athletes available">The leaderboard is currently empty.</StatusPanel></div>
      ) : filteredRows.length === 0 ? (
        <div className="mt-8">
          <StatusPanel title="No matching athletes">
            Try another name or athlete ID.
          </StatusPanel>
        </div>
      ) : (
        <div className="surface-card mt-4 overflow-x-auto">
          <table className="w-full min-w-[780px] table-fixed text-left text-[9px]">
            <thead className="bg-qs-navy text-white">
              <tr>
                <th scope="col" className="w-9 px-1 py-3 text-center">Rank</th>
                <th scope="col" className="w-9 px-1 py-3"><span className="sr-only">Photo</span></th>
                <th scope="col" className="w-24 px-1 py-3">Name</th>
                <th scope="col" className="px-1 py-3 text-center">40 Yard Dash</th>
                <th scope="col" className="px-1 py-3 text-center">Broad Jump</th>
                <th scope="col" className="px-1 py-3 text-center">Vertical Jump</th>
                <th scope="col" className="px-1 py-3 text-center">Pro Agility</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-indigo-100 bg-white">
              {filteredRows.map((row) => {
                const highlighted = isHighlighted(row);
                return (
                  <tr
                    key={row.externalId}
                    ref={highlighted ? highlightRef : undefined}
                    tabIndex={highlighted ? -1 : undefined}
                    className={highlighted ? 'bg-qs-lilac outline outline-2 -outline-offset-2 outline-qs-indigo' : 'transition-colors hover:bg-indigo-50/70'}
                  >
                    <td className="px-1 py-2 text-center font-black">{row.rank ?? '-'}</td>
                    <td className="px-1 py-2">
                      <AthleteAvatar firstName={row.firstName} lastName={row.lastName} imageUrl={row.thumbnailImageUrl} size="sm" className="!size-6 !text-[8px] !ring-0 !shadow-none" />
                    </td>
                    <td className="truncate px-1 py-2 font-black">
                      <span className="block truncate">
                        {getDisplayName(row)}
                        {row.isProfessional && (
                          <span className="ml-1 inline-block rounded bg-qs-violet px-1 py-0.5 text-[7px] font-black text-white">
                            PRO
                          </span>
                        )}
                        {highlighted && <span className="sr-only"> (you)</span>}
                      </span>
                    </td>
                    <td className="px-1 py-2 text-center font-bold">{formatMetric(row.sprint40Seconds, 'sprint40Seconds')}</td>
                    <td className="px-1 py-2 text-center font-bold">{formatMetric(row.broadJumpInches, 'broadJumpInches')}</td>
                    <td className="px-1 py-2 text-center font-bold">{formatMetric(row.verticalJumpInches, 'verticalJumpInches')}</td>
                    <td className="px-1 py-2 text-center font-bold">{formatMetric(row.proAgilitySeconds, 'proAgilitySeconds')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
}
